import { useAsyncFn, useCounter } from 'react-use';
import { captureException } from '@sentry/nextjs';
import i18next from 'i18next';
import { useAtom } from 'jotai';
import useLeadSubmissions from 'lib/hooks/useLeadSubmissions';
import LeadParams from 'types/Apis/LeadParams';
import { ResponseRecord } from 'types/Apis/SearchResponse';
import useEvents from 'lib/hooks/useEvents';
import { useLeadSubmitter } from 'lib/hooks/useLead';
import quickPicksEvent, {
  UserExperience,
} from 'lib/Tracking/Vdp/ListingImpressions/QuickPicksEvent';
import BaseGeocode from 'lib/location/Locations/Base';
import { useEffect, useCallback, useContext, useMemo, useState } from 'react';
import leadFormSubmitEvent from 'lib/Tracking/QuickPicks/LeadFormSubmitEvent';
import favoriteEvent from 'lib/Tracking/Vdp/Quickpicks/FavoriteEvent';
import skipEvent from 'lib/Tracking/Vdp/Quickpicks/SkipEvent';
import VdpEventContext from 'contexts/VdpEventContext';
import QuickPicksEventContext from 'contexts/QuickPicksEventContext';
import viewEvent from 'lib/Tracking/Vdp/Quickpicks/ModalViewEvent';
import { Features } from 'lib/EventSchema';
import useDeviceScreen from 'lib/hooks/useDeviceScreen';
import { fallbackGeoLocationData, useGeolocationData } from 'lib/hooks/useGeoLocation';
import { fetchVehicleWithExperience, FetchVehicleWithExperienceParams } from 'lib/AutolistAPI';
import { extractVehicleWithExperienceParamsFromQuery } from 'lib/hooks/useBuildVdpPageProps';
import useHashLocation from 'lib/hooks/useHashLocation';
import { trackLeadSuccess } from 'lib/Tracking/LeadForm/FormSuccess';
import { EnhancedLeadTrackingPlatform } from 'types/Apis/EnhancedLeadTrackingFields';
import leadInfoAtom from 'atoms/LeadInfoAtom';

import VehicleCard from './VehicleCard';
import EndCard from './EndCard';
import LoadingCard from './LoadingCard';
import styles from './styles';

export interface Props {
  vehicles: ResponseRecord[];
  close: () => void;
  onSkip?: (vehicle: ResponseRecord) => void;
  isLoading: boolean;
}

function useQuickPicksVehicles(vehicles: ResponseRecord[]): {
  advance: () => void;
  vehicle: ResponseRecord;
  index: number;
} {
  const [index, { inc }] = useCounter(0);

  const vehicle = vehicles[index];

  return {
    advance: inc,
    vehicle,
    index,
  };
}

function QuickPicks({ vehicles, close, onSkip, isLoading }: Props): JSX.Element {
  let card: JSX.Element;
  const { vehicle, advance, index } = useQuickPicksVehicles(vehicles);

  const geolocation = useGeolocationData() ?? new BaseGeocode(fallbackGeoLocationData);
  const { isMobile, isTablet } = useDeviceScreen();
  const { values } = useHashLocation();

  const [listingId, setListingId] = useState<number>(0);
  const [leadInfoValues] = useAtom(leadInfoAtom);
  const { adExtId, adPartnerId } = leadInfoValues;

  const leadSubmissions = useLeadSubmissions();
  const lastLeadSubmission = (leadSubmissions[leadSubmissions.length - 1] ||
    undefined) as LeadParams;

  const { trackingParams } = useContext(VdpEventContext);

  const quickPicksEventContext = useContext(QuickPicksEventContext);

  const submitLead = useLeadSubmitter(quickPicksEventContext.sourcePageCategory);
  const [leadSubmissionState, monitoredSubmitLead] = useAsyncFn(submitLead, [submitLead]);

  const apiParams = useMemo<FetchVehicleWithExperienceParams>(() => {
    return {
      latitude: geolocation?.latitude,
      longitude: geolocation?.longitude,
      locale: i18next.language,
      ...extractVehicleWithExperienceParamsFromQuery(values),
      vin: vehicle?.vin,
    };
  }, [geolocation?.latitude, geolocation?.longitude, values, vehicle?.vin]);

  async function fetchVehicleData(): Promise<void> {
    try {
      const vehicleWithExperience = await fetchVehicleWithExperience(apiParams);
      const fetchedListingId = vehicleWithExperience?.vehicle?.listing_id;
      if (fetchedListingId) {
        setListingId(fetchedListingId);
      }
    } catch (error) {
      console.error('Error fetching vehicle experience data:', error);
      captureException(error);
    }
  }

  useEffect(() => {
    if (vehicle?.vin) {
      fetchVehicleData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicle?.vin]);

  const logger = useEvents();
  const logQuickPicksEvent = useCallback(() => {
    vehicles.forEach((v, i) => {
      logger.log(
        quickPicksEvent({
          vin: v.vin,
          trackingParams: v.tracking_params,
          vdpUserExperience: v.experience as UserExperience,
          horizontalPosition: i,
          impressionType: 'result',
          sourcePage: quickPicksEventContext.sourcePage,
        })
      );
    });
  }, [vehicles, logger, quickPicksEventContext.sourcePage]);

  useEffect(() => {
    logQuickPicksEvent();
  }, [logQuickPicksEvent]);

  useEffect(() => {
    logger.log(viewEvent({ trackingParams }));
  }, [logger, trackingParams]);

  if (isLoading) {
    card = <LoadingCard />;
  } else if (vehicle) {
    card = (
      <VehicleCard
        index={index}
        isSubmitting={leadSubmissionState.loading}
        hasSubmitted={Boolean(leadSubmissionState.value?.vin === vehicle.vin)}
        vehicle={vehicle}
        skip={(): void => {
          if (onSkip) onSkip(vehicle);
          logger.log(skipEvent({ trackingParams }));
          advance();
        }}
        onContact={(): void => {
          const sourcePage =
            isMobile || isTablet
              ? quickPicksEventContext.leadTrackingSourceMobile
              : quickPicksEventContext.leadTrackingSourceDesktop;

          monitoredSubmitLead({
            vehicle,
            contactInfo: lastLeadSubmission.lead,
            sourcePage,
            sourceFeature: Features.quickPicks,
            emailAcceptance: {
              search: lastLeadSubmission.accepts_search_emails || false,
              price: lastLeadSubmission.accepts_price_emails || false,
              marketing: lastLeadSubmission.accepts_marketing_emails || false,
            },
            geolocation,
            platform:
              isMobile || isTablet
                ? EnhancedLeadTrackingPlatform.Mobile
                : EnhancedLeadTrackingPlatform.Desktop,
            adExtId,
            adPartnerId,
          }).then((response) => {
            trackLeadSuccess({
              vehicle,
              contactInfo: lastLeadSubmission.lead,
              zip: lastLeadSubmission.lead?.zip,
              leadResponse: response,
              geolocation,
              cplValue: vehicle?.cpl_value,
              price: Number(vehicle.price),
              listingId,
            });
          });
          logger.log(
            leadFormSubmitEvent({
              trackingParams,
              sourcePage: quickPicksEventContext.sourceQP,
            })
          );
          advance();
        }}
        onFavorite={(): void => {
          logger.log(favoriteEvent({ trackingParams }));
          advance();
        }}
      />
    );
  } else {
    card = <EndCard close={close} />;
  }

  return (
    <div className="quick-picks-container">
      {card}
      <style jsx>{styles}</style>
    </div>
  );
}

export default QuickPicks;
